import { Box, Button, Modal, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Avatar } from "../types/General";

type props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  avatars: Avatar[];
  /** Pre-select this avatar (e.g. the one used on a previous generation) */
  initialAvatarId?: string;
  /** Called with the chosen avatar id once the user confirms */
  onConfirm: (avatarId: string) => void;
};

const AvatarPickerModal = ({
  open,
  setOpen,
  avatars,
  initialAvatarId,
  onConfirm,
}: props) => {
  const [selectedId, setSelectedId] = useState<string>("");

  // Reset the selection each time the modal opens: prefer the previously used
  // avatar, else the first one in the catalog.
  useEffect(() => {
    if (!open) return;
    const preset = avatars.find((a) => a.id === initialAvatarId)?.id;
    setSelectedId(preset ?? avatars[0]?.id ?? "");
  }, [open, initialAvatarId, avatars]);

  const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 460,
    maxWidth: "92vw",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow:
      "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    padding: "32px",
    outline: "none",
    border: "1px solid #f1f5f9",
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="avatar-picker-title"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(15, 23, 42, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box sx={style}>
        <Typography
          id="avatar-picker-title"
          variant="h6"
          component="h2"
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#1e293b",
            marginBottom: "6px",
            letterSpacing: "-0.02em",
            textAlign: "center",
          }}
        >
          Choose an avatar
        </Typography>

        <Typography
          sx={{
            fontSize: "14px",
            color: "#64748b",
            lineHeight: 1.5,
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          This avatar narrates the executive summary. Generating uses our
          partner's API credits.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "12px",
            // Cap the width so only 3 cards fit per row → a balanced 3-then-2
            // layout instead of 4-then-1.
            maxWidth: 340,
            margin: "0 auto",
          }}
        >
          {avatars.map((avatar) => {
              const isSelected = avatar.id === selectedId;
              return (
                <Box
                  key={avatar.id}
                  onClick={() => setSelectedId(avatar.id)}
                  sx={{
                    position: "relative",
                    width: 80,
                    cursor: "pointer",
                    borderRadius: "12px",
                    padding: "6px",
                    border: isSelected
                      ? "2px solid #2563eb"
                      : "2px solid #e2e8f0",
                    backgroundColor: isSelected ? "#eff6ff" : "#ffffff",
                    transition: "border-color 0.15s, background-color 0.15s",
                    "&:hover": { borderColor: "#93c5fd" },
                  }}
                >
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        fontSize: 18,
                        color: "#2563eb",
                        backgroundColor: "#ffffff",
                        borderRadius: "50%",
                      }}
                    />
                  )}
                  <Box
                    component="img"
                    src={avatar.imageUrl}
                    alt={avatar.name}
                    sx={{
                      width: "100%",
                      height: 88,
                      objectFit: "cover",
                      objectPosition: "top center",
                      borderRadius: "8px",
                      display: "block",
                      backgroundColor: "#f1f5f9",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#334155",
                      textAlign: "center",
                      marginTop: "6px",
                    }}
                  >
                    {avatar.name}
                  </Typography>
                </Box>
              );
            })}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            marginTop: "24px",
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            sx={{
              color: "#64748b",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              padding: "10px 24px",
              "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>

          <Button
            disabled={!selectedId}
            onClick={() => {
              setOpen(false);
              onConfirm(selectedId);
            }}
            sx={{
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              color: "#ffffff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              padding: "10px 24px",
              boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                boxShadow: "0 6px 20px rgba(37, 99, 235, 0.35)",
              },
              "&.Mui-disabled": { background: "#cbd5e1", color: "#ffffff" },
            }}
          >
            Generate
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AvatarPickerModal;
